import { CHESS_SIDE } from "./constants";
import { TimeCalculator } from "./calculators/TimeCalculator";
import { MoveStatsCalculator } from "./calculators/MoveStatsCalculator";
import { getCommencedAt, getLeewayDuration, hasCommenced, } from "./utils/match-helper";
import { diffIn, toDateWithOffset } from "./utils/date-helper";
export class GameDataBuilder {
    match;
    moves;
    firestoreTimeOffset;
    timeCalculator;
    moveStatsCalculator;
    constructor(match, moves, firestoreTimeOffset = 0) {
        this.match = match;
        this.moves = moves || [];
        this.firestoreTimeOffset = firestoreTimeOffset;
        this.timeCalculator = new TimeCalculator(match, this.moves, firestoreTimeOffset);
        this.moveStatsCalculator = new MoveStatsCalculator(match, this.moves, firestoreTimeOffset);
    }
    /**
     * Calculate leeway time left
     */
    leewayTimeLeft() {
        const lastMove = this.moves.at(-1);
        const lastMoveAt = lastMove ? lastMove.createdAt : null;
        const leewayCheckedAt = lastMoveAt ||
            this.match.startedAt ||
            this.match.createdAt ||
            toDateWithOffset(this.firestoreTimeOffset);
        const leewayElapsed = diffIn(toDateWithOffset(this.firestoreTimeOffset), leewayCheckedAt, "second");
        return getLeewayDuration() - leewayElapsed;
    }
    /**
     * Determine if black starts (from FEN)
     */
    blackToStart() {
        return this.match.initial_fen && this.match.initial_fen.includes(" b ")
            ? 1
            : 0;
    }
    /**
     * Get current turn color
     */
    turnColor() {
        const colorChanged = this.moves.length % 2;
        return (this.blackToStart() + colorChanged) % 2
            ? CHESS_SIDE.BLACK
            : CHESS_SIDE.WHITE;
    }
    /**
     * Get current FEN position
     */
    fen() {
        const lastMove = this.moves.at(-1);
        return lastMove ? lastMove.fen : this.match.initial_fen || "";
    }
    /**
     * Get current PGN
     */
    pgn() {
        const lastMove = this.moves.at(-1);
        return lastMove ? lastMove.pgn : "";
    }
    /**
     * Get game durations
     */
    durations() {
        return this.timeCalculator.calculateDurations();
    }
    /**
     * Get move statistics
     */
    movesStats() {
        return this.moveStatsCalculator.calculateMovesStats();
    }
    /**
     * Get increments
     */
    increments() {
        const movesStats = this.movesStats();
        return this.timeCalculator.calculateIncrements(movesStats);
    }
    /**
     * Get total times
     */
    totalTimes() {
        const durations = this.durations();
        const increments = this.increments();
        return this.timeCalculator.calculateTotalTimes(durations, increments);
    }
    /**
     * Get time offsets
     */
    timeOffsets() {
        return this.timeCalculator.calculateTimeOffsets();
    }
    /**
     * Calculate time left for a specific move
     */
    timeLeftByMove(moveStats) {
        const totalTimes = this.totalTimes();
        return this.timeCalculator.calculateTimeLeftByMove(moveStats, totalTimes);
    }
    /**
     * Calculate time data at specific move index or the last move
     */
    timeData(moveIndex) {
        const movesStats = this.movesStats();
        const lastMoveIndex = movesStats.length ? movesStats.length - 1 : -1;
        const targetIndex = moveIndex === undefined ? lastMoveIndex : moveIndex;
        const move = targetIndex >= 0 ? movesStats[targetIndex] : null;
        const previousMove = targetIndex > 0 ? movesStats[targetIndex - 1] : null;
        let lastWhiteMove = null;
        let lastBlackMove = null;
        if (move) {
            if (move.color === CHESS_SIDE.WHITE) {
                lastWhiteMove = move;
                lastBlackMove = previousMove;
            }
            else {
                lastWhiteMove = previousMove;
                lastBlackMove = move;
            }
        }
        const moveColor = move
            ? move.color
            : this.blackToStart()
                ? CHESS_SIDE.WHITE
                : CHESS_SIDE.BLACK;
        const durations = this.durations();
        const increments = this.increments();
        const totalTimes = this.totalTimes();
        const timeOffsets = this.timeOffsets();
        const startTimerIndex = this.match.tournamentId ? 0 : 2;
        const commenced = hasCommenced(this.match, this.moves);
        const commencedAt = getCommencedAt(this.match, this.moves);
        const timeData = {
            white: {
                ...this.timeLeftByMove(lastWhiteMove),
                duration: durations.white || 0,
                bufferDuration: this.match.buffer_duration,
                increment: increments.white,
                totalTime: totalTimes.white,
                shouldDeductTime: commenced &&
                    movesStats.length >= startTimerIndex &&
                    !this.match.result,
                isNextColor: moveColor === CHESS_SIDE.BLACK,
                lastMoveTime: lastBlackMove?.createdAt ?? commencedAt,
                timeOffset: timeOffsets.white,
            },
            black: {
                ...this.timeLeftByMove(lastBlackMove),
                duration: durations.black || 0,
                bufferDuration: this.match.buffer_duration,
                increment: increments.black,
                totalTime: totalTimes.black,
                shouldDeductTime: commenced &&
                    movesStats.length >= startTimerIndex &&
                    !this.match.result,
                isNextColor: moveColor === CHESS_SIDE.WHITE,
                lastMoveTime: lastWhiteMove?.createdAt ?? commencedAt,
                timeOffset: timeOffsets.black,
            },
        };
        return timeData;
    }
    /**
     * Get complete game data
     */
    gameData() {
        return {
            match: this.match,
            movesStats: this.movesStats(),
            turnColor: this.turnColor(),
            fen: this.fen(),
            pgn: this.pgn(),
            blackToStart: this.blackToStart(),
            timeData: this.timeData(),
            leewayDuration: getLeewayDuration(),
            leewayTimeLeft: this.leewayTimeLeft(),
        };
    }
}
